import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#setupDialogObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.dialogObserver?.disconnect();
    this.#unlockScroll(); // Ensure scroll is unlocked when component is removed
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #setupDialogObserver() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    // Watch for the 'open' attribute changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          if (dialog.hasAttribute('open')) {
            this.#lockScroll();
          } else {
            this.#unlockScroll();
          }
        }
      });
    });

    observer.observe(dialog, { attributes: true });
    this.dialogObserver = observer;

    // Also listen for the native close event as backup
    dialog.addEventListener('close', this.#unlockScroll);
  }

  #lockScroll = () => {
    // Store current scroll position to restore later
    this.scrollPosition = window.pageYOffset;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
  };

  #unlockScroll = () => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    
    // Restore scroll position
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
      this.scrollPosition = undefined;
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => {
        this.close();
      }, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // Override parent methods to ensure scroll unlock
  showDialog() {
    super.showDialog();
  }

  closeDialog() {
    super.closeDialog();
    // Backup unlock in case observer doesn't catch it
    setTimeout(() => this.#unlockScroll(), 0);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}